import {
    Features,
    getFeatureFlagValue,
} from '@experiences/feature-flags';
import urljoin from 'url-join';

import type { IExternalIdentity } from '../../common/interfaces/externalIdentity';
import {
    axiosDelete,
    get,
    post,
    put,
} from '../utility/Requests';
import type {
    DirectoryConnectionType,
    IDirectoryConnectionPayload,
} from './DirectoryConnectionService';
import { getIdentityBaseRoute } from './IdentityServiceUtils';

export const authenticationSettingUrl = `/api/identity/AuthenticationSetting`;

// Helper function to get direct Identity service URL
const getDirectAuthSettingUrl = (partitionGlobalId: string, isHostMode: boolean = false) =>
    `${getIdentityBaseRoute(partitionGlobalId, isHostMode)}/api/AuthenticationSetting`;

export const AuthenticationSettingType = {
    NoSetting: 'no setting',
    AAD: 'aad',
    Google: 'google',
    Microsoft: 'microsoft',
    SAML: 'saml2',
    AD: 'ad',
    LDAPAD: 'ldapad',
};

export const BulkAuthenticationSettingKey = {
    NoSetting: 'no setting',
    AAD: 'aad',
    Google: 'Google',
    Microsoft: 'Microsoft',
    SAML: 'Saml2',
    AD: 'ad',
    LDAPAD: 'ldapad',
};

export interface IAuthenticationSettingPayload {
    type: string;
    displayName: string;
    partitionGlobalId: string;
    externalIdentityProviderId?: number;
    allowedIdp?: string;
    clientId?: string;
    clientSecret?: string;
    tenantId?: string; // multitenant: tenant id from access token in code exchange
    settings?: string;
    department?: string;
    city?: string;
    extensionUserAttributeMappings?: Record<string, string>;
    isScimEnabled?: boolean;
    allowAppOnlyPermissions?: boolean;
    useCachedTokens?: boolean;
}

interface IAuthenticationSettingResponse {
    authenticationSettingType?: string;
    hostConnectionType?: DirectoryConnectionType;
    externalIdentityProviderDto: IExternalIdentity;
    directoryConnectionDto: IDirectoryConnectionPayload;
}

export interface IBulkAuthenticationSettingResponse {
    [key: string]: IAuthenticationSettingResponse;
}

export interface IAuthenticationIDPSettingPayload {
    allowedIdp: string;
    partitionGlobalId: string;
}

interface IScimSettingResponse {
    isScimEnabled: boolean;
    [additionalProperties: string]: unknown;
}

export function getTemplateData(payload: IAuthenticationSettingPayload, isHostMode: boolean = false) {
    const directUrl = `${getDirectAuthSettingUrl(payload.partitionGlobalId, isHostMode)}/GenerateTemplateSettings`;
    return post<IAuthenticationSettingResponse>(directUrl, {
        body: authenticationSettingMapperSaml2(payload),
        ignoreBasePath: true,
    });
}

export function getAllAuthenticationSettings({
    url: _url, partitionGlobalId, isHostMode = false,
}: { url: string; partitionGlobalId: string; isHostMode?: boolean }) {
    const directUrl = `${getDirectAuthSettingUrl(partitionGlobalId, isHostMode)}/getAll/${partitionGlobalId}`;
    return get<IBulkAuthenticationSettingResponse>(directUrl, { ignoreBasePath: true });
}

export function updateIDPSetting(payload: IAuthenticationIDPSettingPayload, isHostMode: boolean = false) {
    const directUrl = `${getDirectAuthSettingUrl(payload.partitionGlobalId, isHostMode)}/IDP`;
    return post<string>(directUrl, {
        body: payload,
        ignoreBasePath: true,
    });
}

export function deleteIDPSetting(partitionGlobalId: string, isHostMode: boolean = false) {
    const directUrl = `${getDirectAuthSettingUrl(partitionGlobalId, isHostMode)}/IDP/${partitionGlobalId}`;
    return axiosDelete(directUrl, { ignoreBasePath: true });
}

export function createDirectoryIntegrationSetting(payload: IAuthenticationSettingPayload, isHostMode: boolean = false) {
    const directUrl = `${getDirectAuthSettingUrl(payload.partitionGlobalId, isHostMode)}/DirectoryIntegration`;
    return post<IAuthenticationSettingResponse>(directUrl, {
        body: authenticationSettingMapper(payload),
        ignoreBasePath: true,
    });
}

export function updateDirectoryIntegrationSetting(payload: IAuthenticationSettingPayload, isHostMode: boolean = false) {
    const directUrl = `${getDirectAuthSettingUrl(payload.partitionGlobalId, isHostMode)}/DirectoryIntegration`;
    return put<IAuthenticationSettingResponse>(directUrl, {
        body: authenticationSettingMapper(payload),
        ignoreBasePath: true,
    });
}

export function createDirectoryIntegrationSettingSaml2(payload: IAuthenticationSettingPayload, isHostMode: boolean = false) {
    const directUrl = `${getDirectAuthSettingUrl(payload.partitionGlobalId, isHostMode)}/DirectoryIntegration`;
    return post<IAuthenticationSettingResponse>(directUrl, {
        body: authenticationSettingMapperSaml2(payload),
        ignoreBasePath: true,
    });
}

export function updateDirectoryIntegrationSettingSaml2(payload: IAuthenticationSettingPayload, isHostMode: boolean = false) {
    const directUrl = `${getDirectAuthSettingUrl(payload.partitionGlobalId, isHostMode)}/DirectoryIntegration`;
    return put<IAuthenticationSettingResponse>(directUrl, {
        body: authenticationSettingMapperSaml2(payload),
        ignoreBasePath: true,
    });
}

export function deleteDirectoryIntegrationSetting(partitionGlobalId: string, isHostMode: boolean = false) {
    const directUrl = `${getDirectAuthSettingUrl(partitionGlobalId, isHostMode)}/DirectoryIntegration/${partitionGlobalId}`;
    return axiosDelete(directUrl, { ignoreBasePath: true });
}

export function updateDirectoryIntegrationScimSetting(partitionGlobalId: string, isHostMode: boolean, isScimEnabled: boolean) {
    return put<IScimSettingResponse>(
        urljoin(getIdentityBaseRoute(partitionGlobalId, isHostMode), 'api/AuthenticationSetting/DirectoryIntegration/Scim', partitionGlobalId),
        {
            body: { enabled: isScimEnabled },
            ignoreBasePath: true,
        }
    );
}

export function authenticationSettingMapper(payload: IAuthenticationSettingPayload) {
    const bodyPayload: any = {
        ...payload,
        authenticationScheme: 'oidc',
        isActive: true,
        isExclusive: true,
    };

    if (!bodyPayload.allowedIdp) {
        const tenantId = bodyPayload.tenantId.trim();
        bodyPayload.logoutUrl = 'https://login.microsoftonline.com/common/oauth2/v2.0/logout';
        bodyPayload.authority = `https://login.microsoftonline.com/${tenantId}/v2.0/`;
        bodyPayload.settings = JSON.stringify({ PostLogoutRedirectUriParameter: 'post_logout_redirect_uri' });

        const configuration: any = {
            ClientId: payload.clientId,
            ClientSecret: payload.clientSecret,
            TenantId: tenantId,
            AADProvisioningAttributeMapper: {
                Department: payload.department,
                City: payload.city,
                ExtensionUserAttributeMappings: payload.extensionUserAttributeMappings,
            },
        };

        // Only include AllowAppOnlyPermissions if explicitly provided
        if (getFeatureFlagValue(Features.EnableEntraAccessScope.name) && payload.allowAppOnlyPermissions !== undefined) {
            configuration.AllowAppOnlyPermissions = payload.allowAppOnlyPermissions;
        }

        // Only include AllowCachedAADToken if the feature flag is enabled and value is provided
        if (getFeatureFlagValue(Features.EnableEntraAccessScope.name)
            && getFeatureFlagValue(Features.EnableEntraCachedTokens.name)
            && payload.useCachedTokens !== undefined) {
            configuration.AllowCachedAADToken = payload.useCachedTokens;
        }

        bodyPayload.configuration = JSON.stringify(configuration);

        delete bodyPayload['tenantId'];
    }

    return bodyPayload;
}

export function authenticationSettingMapperSaml2(payload: IAuthenticationSettingPayload) {
    const bodyPayload: any = {
        ...payload,
        authenticationScheme: 'Saml2',
        isActive: true,
        isExclusive: true,
    };

    return bodyPayload;
}
